import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Login from './pages/Login';
import Register from './pages/Register';
import UserApproval from './pages/UserApproval';
import Dashboard from './pages/Dashboard/Dashboard.tsx';
import Dominios from './pages/Dominios';
import Profile from './pages/Profile';
import Reward from './pages/Reward/Reward.tsx';
import MainLayout from './components/layout/MainLayout';
import { Toaster } from './components/ui/toaster';
import 'react-day-picker/dist/style.css';
import Blocos from "./pages/Blocks/Blocks.tsx";
import Setup from "@/pages/Setup/Setup.tsx";
import Reports from "@/pages/Reports/Reports.tsx";
import Ltv from "@/pages/Ltv/Ltv.tsx";
import CustomTargeting from "@/pages/CustomTargeting.tsx";
import SystemHealth from "@/pages/SystemHealth.tsx";
import { CreatePriceRule, CreateBulkPriceRules, PriceRules, Templates } from "@/pages/PriceRules";
import { PriceCascades } from "@/pages/PriceCascades";
import { CascadeReport } from '@/pages/CascadeReport';
import { PriceStructure, ConfigureGrid, PriceStructureResults } from "@/pages/PriceStructure";
import { CascadeBlocks } from "@/pages/CascadeBlocks";
import { PushDashboard, PushSetup, PushCampaigns, PushLeads, PushFlows, PushGroups } from '@/pages/PushNotification';
import CampaignCreate from "@/pages/PushNotification/Campaigns/CampaignCreate.tsx";
import CampaignBatch from "@/pages/PushNotification/Campaigns/CampaignBatch.tsx";
import FlowBatch from "@/pages/PushNotification/Flows/FlowBatch.tsx";
import Urls from "@/pages/Urls/Urls.tsx";
import InventoryRules from "@/pages/InventoryRules/InventoryRules.tsx";
import Broadcast from '@/pages/Meta/Broadcast/Broadcast.tsx';
import BroadcastRetries from '@/pages/Meta/Broadcast/BroadcastRetries.tsx';
import MetaAnalyticsV2 from '@/pages/Meta/AnalyticsV2/MetaAnalyticsV2';
import BroadcastEditorPage from '@/pages/Meta/Broadcast/BroadcastEditorPage.tsx';
import Connection from '@/pages/Meta/Connection/Connection.tsx';
import MetaCallback from '@/pages/Meta/Connection/MetaCallback.tsx';
import PageDetails from '@/pages/Meta/Connection/PageDetails.tsx';
import Funnels from '@/pages/Meta/Flow/Funnel/Funnels.tsx';
import FlowBuilder from '@/pages/Meta/Flow/Builder/Builder.tsx';
import PageDebug from '@/pages/Meta/PageDebug/PageDebug.tsx';
import Leads from '@/pages/Meta/Leads/Leads.tsx';
import LeadsDashboard from '@/pages/Meta/LeadsDashboard/LeadsDashboard.tsx';
import MessengerTemplates from '@/pages/Meta/MessengerTemplates/MessengerTemplates.tsx';
import UtilityTemplates from '@/pages/Meta/UtilityTemplates/UtilityTemplates.tsx';
import UtilityEsteiras from '@/pages/Meta/UtilityEsteiras/UtilityEsteiras.tsx';
import EsteiraBuilder from '@/pages/Meta/UtilityEsteiras/EsteiraBuilder.tsx';
import BroadcastTemplates from '@/pages/Meta/BroadcastTemplates/BroadcastTemplates.tsx';
import CommentAutoReplies from '@/pages/Meta/CommentAutoReplies/CommentAutoReplies.tsx';
import CommentAutoReplyBuilder from '@/pages/Meta/CommentAutoReplies/Builder/CommentAutoReplyBuilder.tsx';
import ChatfoodSync from '@/pages/Meta/ChatfoodSync/ChatfoodSync.tsx';
import UtmFix from '@/pages/Meta/UtmFix/UtmFix.tsx';
import AiKeys from '@/pages/Settings/AiKeys.tsx';
import PageAccess from '@/pages/Settings/PageAccess/PageAccess.tsx';
import DomainAccess from '@/pages/Settings/DomainAccess/DomainAccess.tsx';
import {RedirectGroups} from "@/pages/RedirectGroups";
import {Pageview} from "@/pages/Pageview";
import { ChatbotSubdomains, ChatbotBuilder } from "@/pages/Chatbot";
import { getCurrentUser } from '@/services/api';
import { podeVerChatbot, podeVerMetaAds } from '@/lib/acessoRestrito';

const V2MetaApp = lazy(() => import('./v2/V2MetaApp'));
const PlatformApp = lazy(() => import('./v2/pages/Platform'));

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5,
            gcTime: 1000 * 60 * 60 * 24,
        },
    },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const token = localStorage.getItem('access_token');
    if (!token) {
        return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
}

function MetaRoute({ children }: { children: React.ReactNode }) {
    if (!podeVerMetaAds(getCurrentUser())) {
        return <Navigate to="/" replace />;
    }
    return <>{children}</>;
}

function ChatbotRoute({ children }: { children: React.ReactNode }) {
    if (!podeVerChatbot(getCurrentUser())) {
        return <Navigate to="/" replace />;
    }
    return <>{children}</>;
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route
                        path="/plataforma/*"
                        element={
                            <Suspense fallback={null}>
                                <PlatformApp />
                            </Suspense>
                        }
                    />
                    <Route path="/callback" element={<MetaCallback />} />
                    <Route path="/connect/meta/callback" element={<MetaCallback />} />

                    {/* Redesign v2 do Meta Ads: layout próprio, fora do MainLayout. Não substitui /meta/*. */}
                    <Route
                        path="/v2/meta/*"
                        element={
                            <ProtectedRoute>
                                <MetaRoute>
                                    <Suspense fallback={null}>
                                        <V2MetaApp />
                                    </Suspense>
                                </MetaRoute>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        element={
                            <ProtectedRoute>
                                <MainLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/domain" element={<Dominios />} />
                        <Route path="/offerwall" element={<Reward />} />
                        <Route path="/blocks" element={<Blocos />} />
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/ltv" element={<Ltv />} />
                        <Route path="/custom-targeting" element={<CustomTargeting />} />
                        <Route path="/system-health" element={<SystemHealth />} />
                        <Route path="/setup/:domainId" element={<Setup />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/price-rules" element={<PriceRules />} />
                        <Route path="/price-rules/create" element={<CreatePriceRule />} />
                        <Route path="/price-rules/create-bulk" element={<CreateBulkPriceRules />} />
                        <Route path="/price-rules/templates" element={<Templates />} />
                        <Route path="/price-cascades" element={<PriceCascades />} />
                        <Route path="/cascade-report" element={<CascadeReport />} />
                        <Route path="/price-structure" element={<PriceStructure />} />
                        <Route path="/price-structure/configure" element={<ConfigureGrid />} />
                        <Route path="/price-structure/results/:domainId" element={<PriceStructureResults />} />
                        <Route path="/cascade-blocks" element={<CascadeBlocks />} />
                        <Route path="/urls" element={<Urls />} />
                        <Route path="/inventory-rules" element={<InventoryRules />} />
                        <Route path="/pageview" element={<Pageview />} />
                        <Route path="/pageview/redirect-groups" element={<RedirectGroups />} />
                        <Route path="/redirect-groups" element={<RedirectGroups />} />

                        {/* Meta */}
                        <Route path="/meta/analytics" element={<MetaRoute><MetaAnalyticsV2 /></MetaRoute>} />
                        <Route path="/meta/broadcasts" element={<MetaRoute><Broadcast /></MetaRoute>} />
                        <Route path="/meta/broadcasts/retries" element={<MetaRoute><BroadcastRetries /></MetaRoute>} />
                        <Route path="/meta/broadcasts/page/:pageId" element={<MetaRoute><BroadcastEditorPage /></MetaRoute>} />
                        <Route path="/meta/connection" element={<MetaRoute><Connection /></MetaRoute>} />
                        <Route path="/meta/connection/pages/:id" element={<MetaRoute><PageDetails /></MetaRoute>} />
                        <Route path="/meta/flow" element={<MetaRoute><Funnels /></MetaRoute>} />
                        <Route path="/meta/flow/:funnelId" element={<MetaRoute><FlowBuilder /></MetaRoute>} />
                        <Route path="/meta/page-debug" element={<MetaRoute><PageDebug /></MetaRoute>} />
                        {/* LEGADO: fora do menu desde 25/09/2026, substituída por
                            /meta/leads-dashboard. A rota fica de pé por causa de
                            links salvos — ver o cabeçalho de Leads.tsx. */}
                        <Route path="/meta/leads" element={<MetaRoute><Leads /></MetaRoute>} />
                        <Route path="/meta/leads-dashboard" element={<MetaRoute><LeadsDashboard /></MetaRoute>} />
                        <Route path="/meta/messenger-templates" element={<MetaRoute><MessengerTemplates /></MetaRoute>} />
                        <Route path="/meta/utility-templates" element={<MetaRoute><UtilityTemplates /></MetaRoute>} />
                        <Route path="/meta/utility-esteiras" element={<MetaRoute><UtilityEsteiras /></MetaRoute>} />
                        <Route path="/meta/utility-esteiras/new" element={<MetaRoute><EsteiraBuilder /></MetaRoute>} />
                        <Route path="/meta/utility-esteiras/:id" element={<MetaRoute><EsteiraBuilder /></MetaRoute>} />
                        <Route path="/meta/broadcast-templates" element={<MetaRoute><BroadcastTemplates /></MetaRoute>} />
                        <Route path="/meta/comment-auto-replies" element={<MetaRoute><CommentAutoReplies /></MetaRoute>} />
                        <Route path="/meta/comment-auto-replies/:ruleId" element={<MetaRoute><CommentAutoReplyBuilder /></MetaRoute>} />
                        <Route path="/meta/sync" element={<MetaRoute><ChatfoodSync /></MetaRoute>} />
                        <Route path="/meta/utm-fix" element={<MetaRoute><UtmFix /></MetaRoute>} />

                        {/* Settings */}
                        <Route path="/settings/ai-keys" element={<AiKeys />} />
                        <Route path="/settings/page-access" element={<PageAccess />} />
                        <Route path="/settings/domain-access" element={<DomainAccess />} />
                        <Route path="/settings/users" element={<UserApproval />} />

                        {/* Push Notification */}
                        <Route path="/push" element={<PushDashboard />} />
                        <Route path="/push/setup" element={<PushSetup />} />
                        <Route path="/push/campaigns" element={<PushCampaigns />} />
                        <Route path="/push/leads" element={<PushLeads />} />
                        <Route path="/push/flows" element={<PushFlows />} />
                        <Route path="/push/groups" element={<PushGroups />} />
                        <Route path="/push/campaigns/new" element={<CampaignCreate />} />
                        <Route path="/push/campaigns/batch" element={<CampaignBatch />} />
                        <Route path="/push/flows/batch" element={<FlowBatch />} />

                        {/* Chatbot */}
                        <Route path="/chatbot" element={<ChatbotRoute><ChatbotSubdomains /></ChatbotRoute>} />
                        <Route path="/chatbot/:subdomainId" element={<ChatbotRoute><ChatbotBuilder /></ChatbotRoute>} />
                    </Route>

                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                <Toaster />
            </BrowserRouter>
        </QueryClientProvider>
    );
}

export default App;